// Firebase Authentication service for the Team Time Tracker
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth } from './firebase';

// Authentication service
export const authService = {
  // Sign in with email and password
  async signIn(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return {
        user: userCredential.user,
        success: true,
        error: null
      };
    } catch (error) {
      console.error('Error signing in:', error);
      return {
        user: null,
        success: false,
        error: error.message
      };
    }
  },

  // Create a new account
  async signUp(email, password, displayName) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update the user's display name
      if (displayName) {
        await updateProfile(userCredential.user, {
          displayName: displayName
        });
      }
      
      return {
        user: userCredential.user,
        success: true,
        error: null
      };
    } catch (error) {
      console.error('Error creating account:', error);
      return {
        user: null,
        success: false,
        error: error.message
      };
    }
  },

  // Sign out
  async signOut() {
    try {
      await signOut(auth);
      return { success: true, error: null };
    } catch (error) {
      console.error('Error signing out:', error);
      return { success: false, error: error.message };
    }
  },

  // Listen to authentication state changes
  onAuthStateChange(callback) {
    return onAuthStateChanged(auth, callback);
  },

  // Get current user
  getCurrentUser() {
    return auth.currentUser;
  },

  // Send password reset email
  async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true, error: null };
    } catch (error) {
      console.error('Error sending password reset:', error);
      return { success: false, error: error.message };
    }
  },

  // Update user password
  async updateUserPassword(currentPassword, newPassword) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user logged in');
      }

      // Re-authenticate user before updating password
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);
      return { success: true, error: null };
    } catch (error) {
      console.error('Error updating password:', error);
      return { success: false, error: error.message };
    }
  },

  // Update user profile
  async updateUserProfile(updates) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user logged in');
      }

      await updateProfile(user, updates);
      return { success: true, error: null };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { success: false, error: error.message };
    }
  },

  // Sign in with Google
  async signInWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      return {
        user: userCredential.user,
        success: true,
        error: null
      };
    } catch (error) {
      console.error('Error signing in with Google:', error);
      return {
        user: null,
        success: false,
        error: error.message
      };
    }
  }
};

// Simple authentication service for backward compatibility with your current password system
export const simpleAuthService = {
  // Simple password-based authentication (for backward compatibility)
  async authenticate(password) {
    // This is a simple implementation that matches your current system
    // In production, you should use proper Firebase Authentication
    if (password === 'BurhanER') {
      return { success: true, error: null };
    } else {
      return { success: false, error: 'Incorrect password!' };
    }
  },

  // Check if user is authenticated (for simple auth)
  isAuthenticated() {
    // In a real implementation, this would check Firebase auth state
    // For now, we'll use localStorage to persist the auth state
    return localStorage.getItem('simpleAuth') === 'true';
  },

  // Set authentication state (for simple auth)
  setAuthenticated(isAuth) {
    if (isAuth) {
      localStorage.setItem('simpleAuth', 'true');
    } else {
      localStorage.removeItem('simpleAuth');
    }
  },

  // Clear authentication state
  clearAuth() {
    localStorage.removeItem('simpleAuth');
  }
};

export default authService;
